/**
 * Where the attach picker finds candidates of each type.
 *
 * ONE table, consumed by both the web picker and the Expo one. The alternative was a
 * hand-maintained copy on each side plus a "drift test" — which cannot actually be
 * written, because the mobile Jest config resolves exactly one non-relative package
 * (`@mn/shared`) and the web hook imports `@app/lib/*`. A shared table makes drift a
 * compile error instead of an assertion nobody can express.
 *
 * Declarative on purpose: a path template, the array key in the response, and which
 * fields make a human-readable label. Anything richer (query builders, extractors) would
 * drag client-specific helpers into a package both clients import.
 *
 * NOT every attachable type is here. `BlueprintAttachmentEntityType` has 18 members and a
 * panel can file against all of them; this table only covers the ones with a
 * project-scoped list endpoint to enumerate. Offering a type with no way to list its rows
 * renders an empty dropdown, which reads as broken.
 */
import type { BlueprintAttachmentEntityType } from './enums.js';

export interface AttachmentPickerTarget {
  entityType: BlueprintAttachmentEntityType;
  /** `:projectId` is substituted; anything else is a literal path segment. */
  path: string;
  /** True when the project goes in the query string rather than the path. */
  projectIdInQuery: boolean;
  /** Array key inside the response body. */
  responseKey: string;
  /**
   * Fields joined with an em dash to label a row, in order. A missing field is skipped,
   * so a two-field label degrades to one rather than rendering "undefined — x".
   */
  labelFields: readonly string[];
}

export const ATTACHMENT_PICKER_TARGETS: readonly AttachmentPickerTarget[] = [
  {
    entityType: 'RFI',
    path: '/construction/rfis',
    projectIdInQuery: true,
    responseKey: 'rfis',
    labelFields: ['question'],
  },
  {
    entityType: 'DAILY_LOG',
    path: '/construction/daily-logs',
    projectIdInQuery: true,
    responseKey: 'dailyLogs',
    labelFields: ['workPerformed'],
  },
  {
    entityType: 'PUNCH_ITEM',
    path: '/construction/punch-list',
    projectIdInQuery: true,
    responseKey: 'items',
    labelFields: ['location', 'description'],
  },
  {
    entityType: 'CHANGE_ORDER',
    path: '/construction/change-orders',
    projectIdInQuery: true,
    responseKey: 'changeOrders',
    labelFields: ['title'],
  },
  {
    entityType: 'DECISION',
    path: '/construction/decision-journal',
    projectIdInQuery: true,
    responseKey: 'entries',
    labelFields: ['title'],
  },
  {
    entityType: 'MINISTRY_SPACE',
    path: '/construction/ministry-spaces',
    projectIdInQuery: true,
    responseKey: 'spaces',
    labelFields: ['name'],
  },
  {
    entityType: 'PERMIT',
    path: '/construction/projects/:projectId/permits',
    projectIdInQuery: false,
    responseKey: 'permits',
    labelFields: ['title'],
  },
  {
    entityType: 'TASK',
    path: '/construction/projects/:projectId/tasks',
    projectIdInQuery: false,
    responseKey: 'tasks',
    labelFields: ['title'],
  },
  {
    // Note the response key is `plans`, not `floorPlans`.
    entityType: 'FLOOR_PLAN',
    path: '/construction/projects/:projectId/floorplans',
    projectIdInQuery: false,
    responseKey: 'plans',
    labelFields: ['title'],
  },
  {
    entityType: 'SPACE',
    path: '/construction/projects/:projectId/spaces',
    projectIdInQuery: false,
    responseKey: 'spaces',
    labelFields: ['spaceCode', 'name'],
  },
  {
    entityType: 'PHASE',
    path: '/construction/projects/:projectId/phases',
    projectIdInQuery: false,
    responseKey: 'phases',
    labelFields: ['name'],
  },
  {
    entityType: 'MILESTONE',
    path: '/construction/projects/:projectId/milestones',
    projectIdInQuery: false,
    responseKey: 'milestones',
    labelFields: ['title'],
  },
];

/** The types the picker offers, in display order. */
export const ATTACHMENT_PICKABLE_TYPES: readonly BlueprintAttachmentEntityType[] =
  ATTACHMENT_PICKER_TARGETS.map((target) => target.entityType);

export function findAttachmentPickerTarget(
  entityType: BlueprintAttachmentEntityType,
): AttachmentPickerTarget | undefined {
  return ATTACHMENT_PICKER_TARGETS.find((target) => target.entityType === entityType);
}

/** Build the request path for one target. */
export function attachmentCandidatePath(
  target: AttachmentPickerTarget,
  projectId: string,
): string {
  return target.projectIdInQuery
    ? `${target.path}?projectId=${encodeURIComponent(projectId)}`
    : target.path.replace(':projectId', encodeURIComponent(projectId));
}

/** Join the configured label fields of a candidate row. */
export function attachmentCandidateLabel(
  target: AttachmentPickerTarget,
  row: Record<string, unknown>,
): string {
  const parts = target.labelFields
    .map((field) => row[field])
    .filter((value): value is string => typeof value === 'string' && value.trim() !== '');
  return parts.join(' — ');
}
